import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import PageLayout from '../../components/layout/PageLayout/PageLayout';
import { useAuth } from '../../contexts/AuthContext';
import { C, ff, t } from '../../shared/constants/dashboardConstants';
import styles from './CommunityPage.module.css';
import {
  getPosts, getPost, createPost, createComment,
  updatePost, deletePost, deleteComment,
  togglePostLike, toggleCommentLike,
  markPostRead, getReadPostIds,
  toApiCategory, normalizePost, normalizeComment,
  votePoll, cancelVotePoll,
  pinPost, unpinPost,
} from '../../api/communityApi';
import { uploadFile } from '../../api/fileApi';
import { loadActiveAds } from '../../shared/hooks/useAds';
import { CommunityAdBadge } from '../../shared/components/AdBanner';

const CATEGORIES = ['전체', '공지사항', '자유게시판', 'QnA', 'Castar Ads'];
const ADS_CATEGORY = 'Castar Ads';

/** 광고 데이터를 커뮤니티 목록 카드와 동일한 필드 모양으로 맞춘다 (실제 게시글 API와 분리) */
function adToPostShape(ad) {
  return {
    id: ad.id,
    isAd: true,
    category: ADS_CATEGORY,
    title: ad.title,
    content: ad.subtitle,
    author: ad.advertiser,
    authorInitial: 'c',
    authorProfileImage: null,
    pinned: false,
    likes: ad.likeCount ?? 0,
    isLiked: false,
    views: ad.viewCount ?? 0,
    date: ad.postedAt,
    commentCount: 0,
    hasPoll: false,
  };
}
const LOADING_GIF = '/assets/images/Castar_web_Loading_motion_black_v2.gif';

// localStorage에 저장된 읽음 ID Set — 앱 전체 공유
function loadReadIds() {
  try { return new Set(JSON.parse(localStorage.getItem('community_read') || '[]')); } catch { return new Set(); }
}
function saveReadIds(set) {
  try { localStorage.setItem('community_read', JSON.stringify([...set])); } catch {}
}

// ─── Icons ───────────────────────────────────────────────────────────────────

const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <circle cx="11" cy="11" r="8" stroke={C.g300} strokeWidth="1.8" />
    <path d="M21 21l-4.35-4.35" stroke={C.g300} strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M18 6L6 18M6 6l12 12" stroke={C.g400} strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const ShareIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <circle cx="18" cy="5" r="3" stroke={C.g400} strokeWidth="1.8" />
    <circle cx="6" cy="12" r="3" stroke={C.g400} strokeWidth="1.8" />
    <circle cx="18" cy="19" r="3" stroke={C.g400} strokeWidth="1.8" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" stroke={C.g400} strokeWidth="1.8" strokeLinecap="round" />
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" stroke={C.g400} strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const EyeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke={C.g300} strokeWidth="1.8" />
    <circle cx="12" cy="12" r="3" stroke={C.g300} strokeWidth="1.8" />
  </svg>
);

const HeartIcon = ({ filled, color }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill={filled ? (color || '#EF4444') : 'none'}>
    <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" stroke={filled ? (color || '#EF4444') : C.g300} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CommentIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2v10z" stroke={C.g300} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ─── Write Button ─────────────────────────────────────────────────────────────

function WriteButton({ onClick, dark, label = '글쓰기' }) {
  if (dark) {
    return (
      <button onClick={onClick} style={{ marginTop: '4px', padding: '11px 28px', borderRadius: '10px', border: 'none', background: 'linear-gradient(101.37deg, #FFD92B 0.67%, #FFB517 65.65%)', cursor: 'pointer', ...t('14px', 600, C.bk), transition: 'all 0.2s ease' }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = '0px 0px 40px rgba(255, 255, 255, 0.57)';
          e.currentTarget.style.transform = 'translateY(-2px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = 'none';
          e.currentTarget.style.transform = 'translateY(0)';
        }}>
        {label}
      </button>
    );
  }
  return (
    <button onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 16px', border: 'none', borderRadius: '10px', background: 'linear-gradient(101.37deg, #FFD92B 0.67%, #FFB517 65.65%)', cursor: 'pointer', flexShrink: 0, ...t('14px', 600, C.bk), transition: 'all 0.2s ease' }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0px 0px 40px rgba(255, 255, 255, 0.57)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.transform = 'translateY(0)';
      }}>
      <EditIcon />
      {label}
    </button>
  );
}

// ─── Avatar ───────────────────────────────────────────────────────────────────

function Avatar({ initial, image, size = 32 }) {
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = image && !imageFailed;

  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: C.g200, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden', ...t(`${Math.round(size * 0.4)}px`, 600, C.g500) }}>
      {showImage ? (
        <img
          src={image}
          alt=""
          loading="lazy"
          decoding="async"
          width={size}
          height={size}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          onError={() => setImageFailed(true)}
        />
      ) : (
        initial
      )}
    </div>
  );
}

// ─── Tag Badge ────────────────────────────────────────────────────────────────

function Tag({ label, variant = 'default' }) {
  const styleMap = { default: { bg: C.g200, color: C.bk }, hot: { bg: '#FF4040', color: C.white }, new: { bg: C.point, color: C.bk } };
  const s = styleMap[variant] || styleMap.default;
  return (
    <span style={{ padding: '4px 10px', borderRadius: '6px', background: s.bg, ...t('14px', 600, s.color) }}>
      {label}
    </span>
  );
}

// ─── Loading Spinner ──────────────────────────────────────────────────────────

function ListLoader() {
  return (
    <div className={styles.loader}>
      <img src={LOADING_GIF} alt="로딩 중" style={{ width: '80px', height: '80px', objectFit: 'contain' }} />
    </div>
  );
}

// ─── Write Panel (인라인 에디터) ──────────────────────────────────────────────

function guessFileType(file) {
  if (file.type.startsWith('image/')) return 'IMAGE';
  if (file.type.startsWith('video/')) return 'VIDEO';
  return 'DOCUMENT';
}

function WritePanel({ onClose, onSubmit, loading, isAdmin, isQnaAdmin }) {
  const [category, setCategory] = useState('자유게시판');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [showPoll, setShowPoll] = useState(false);
  const [pollType, setPollType] = useState('text');
  const [poll, setPoll] = useState({ question: '', options: ['', '', ''], allowMultiple: false, anonymous: false, allowAddOptions: false, endsAt: '' });
  const [attachments, setAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef();
  const charLimit = 1000;
  const availableCategories = (isAdmin || isQnaAdmin) ? ['공지사항', '자유게시판', 'QnA'] : ['자유게시판', 'QnA'];

  const togglePoll = () => {
    if (showPoll) {
      setShowPoll(false);
      setPoll({ question: '', options: ['', '', ''], allowMultiple: false, anonymous: false, allowAddOptions: false, endsAt: '' });
      setPollType('text');
    } else {
      setShowPoll(true);
    }
  };

  const updateOption = (i, val) => setPoll(p => ({ ...p, options: p.options.map((o, idx) => idx === i ? val : o) }));
  const addOption = () => { if (poll.options.length < 5) setPoll(p => ({ ...p, options: [...p.options, ''] })); };
  const removeOption = (i) => { if (poll.options.length > 2) setPoll(p => ({ ...p, options: p.options.filter((_, idx) => idx !== i) })); };

  const handleFiles = (e) => {
    const files = Array.from(e.target.files);
    setAttachments(prev => [...prev, ...files.map(f => ({ id: Date.now() + Math.random(), file: f }))]);
    e.target.value = '';
  };

  const handleSubmit = async () => {
    if (!title.trim()) { alert('제목을 입력해 주세요.'); return; }
    if (!content.trim()) { alert('내용을 입력해 주세요.'); return; }
    if (showPoll) {
      if (!poll.question.trim()) { alert('투표 제목을 입력해 주세요.'); return; }
      if (poll.options.filter(o => o.trim()).length < 2) { alert('투표 항목을 2개 이상 입력해 주세요.'); return; }
    }
    setSubmitting(true);
    try {
      let fileIds = [];
      if (attachments.length > 0) {
        const results = await Promise.all(attachments.map(({ file }) => uploadFile(file, guessFileType(file))));
        fileIds = results.map(r => r.fileEntityId);
      }
      await onSubmit({
        title: title.trim(),
        content: content.trim(),
        category,
        ...(fileIds.length > 0 && { fileIds }),
        ...(showPoll && { poll: { ...poll, options: poll.options.filter(o => o.trim()), endsAt: poll.endsAt || undefined } }),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const isSubmitting = loading || submitting;

  return (
    <div style={{ background: C.white, border: `1px solid ${C.g200}`, borderRadius: '20px', marginBottom: '16px', overflow: 'hidden' }}>
      {/* 헤더: 카테고리 탭 + 투표 버튼 + 닫기 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '14px 20px', borderBottom: `1px solid ${C.g200}` }}>
        {availableCategories.map(cat => (
          <button key={cat} onClick={() => setCategory(cat)} style={{ padding: '6px 14px', borderRadius: '8px', cursor: 'pointer', border: `1.5px solid ${category === cat ? C.bk : C.g200}`, background: category === cat ? C.bk : C.white, ...t('13px', 600, category === cat ? C.white : C.g400) }}>{cat}</button>
        ))}
        <span style={{ flex: 1 }} />
        <button onClick={togglePoll} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 14px', borderRadius: '8px', cursor: 'pointer', border: `1.5px solid ${showPoll ? C.bk : C.g200}`, background: showPoll ? C.bk : C.white, ...t('13px', 600, showPoll ? C.white : C.g400) }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><rect x="2" y="13" width="4" height="9" rx="1" /><rect x="9" y="8" width="4" height="14" rx="1" /><rect x="16" y="3" width="4" height="19" rx="1" /></svg>
          투표
        </button>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, marginLeft: '4px' }}><CloseIcon /></button>
      </div>

      {/* 본문 */}
      <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <input value={title} onChange={e => setTitle(e.target.value)} placeholder="제목 입력" maxLength={100} style={{ width: '100%', padding: '12px 16px', border: `1px solid ${C.g200}`, borderRadius: '10px', fontFamily: ff, fontSize: '15px', fontWeight: 500, letterSpacing: '-0.04em', outline: 'none', boxSizing: 'border-box', color: C.bk }} />
        <textarea value={content} onChange={e => { if (e.target.value.length <= charLimit) setContent(e.target.value); }} placeholder="내용 입력" style={{ width: '100%', padding: '12px 16px', border: `1px solid ${C.g200}`, borderRadius: '10px', fontFamily: ff, fontSize: '14px', letterSpacing: '-0.04em', outline: 'none', resize: 'none', boxSizing: 'border-box', minHeight: '120px', color: C.bk }} />

        {/* 투표 섹션 */}
        {showPoll && (
          <div style={{ border: `1px solid ${C.g200}`, borderRadius: '12px', overflow: 'hidden' }}>
            {/* 투표 타입 탭 + 닫기 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 14px', borderBottom: `1px solid ${C.g200}` }}>
              {[['텍스트', 'text'], ['날짜', 'date']].map(([label, val]) => (
                <button key={val} onClick={() => setPollType(val)} style={{ padding: '5px 12px', borderRadius: '6px', cursor: 'pointer', border: 'none', background: pollType === val ? C.bk : C.g200, ...t('13px', 600, pollType === val ? C.white : C.g400) }}>{label}</button>
              ))}
              <span style={{ flex: 1 }} />
              <button onClick={togglePoll} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><CloseIcon /></button>
            </div>
            <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <input value={poll.question} onChange={e => setPoll(p => ({ ...p, question: e.target.value }))} placeholder="투표 제목" style={{ width: '100%', padding: '10px 14px', border: `1px solid ${C.g200}`, borderRadius: '8px', fontFamily: ff, fontSize: '14px', outline: 'none', boxSizing: 'border-box', color: C.bk }} />
              {poll.options.map((opt, i) => (
                <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input value={opt} onChange={e => updateOption(i, e.target.value)} placeholder="항목 입력" type={pollType === 'date' ? 'date' : 'text'} style={{ flex: 1, padding: '10px 14px', border: `1px solid ${C.g200}`, borderRadius: '8px', fontFamily: ff, fontSize: '14px', outline: 'none', boxSizing: 'border-box', color: C.bk }} />
                  <button onClick={() => removeOption(i)} disabled={poll.options.length <= 2} style={{ background: 'none', border: 'none', cursor: poll.options.length <= 2 ? 'default' : 'pointer', padding: 6, opacity: poll.options.length <= 2 ? 0.3 : 1, flexShrink: 0 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" stroke={C.g400} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </button>
                </div>
              ))}
              {poll.options.length < 5 && (
                <button onClick={addOption} style={{ border: `1px dashed ${C.g200}`, borderRadius: '8px', background: 'none', padding: '10px', cursor: 'pointer', width: '100%', ...t('13px', 500, C.g300) }}>+ 항목 추가</button>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {[['allowMultiple', '복수선택'], ['anonymous', '익명투표'], ['allowAddOptions', '선택항목 추가 허용']].map(([key, label]) => (
                  <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', ...t('14px', 400, C.bk) }}>
                    <input type="checkbox" checked={poll[key]} onChange={e => setPoll(p => ({ ...p, [key]: e.target.checked }))} style={{ width: 15, height: 15, cursor: 'pointer', accentColor: C.bk }} />
                    {label}
                  </label>
                ))}
              </div>
              <div>
                <div style={{ ...t('13px', 600, C.bk), marginBottom: '8px' }}>투표 종료시간 설정</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', border: `1px solid ${C.g200}`, borderRadius: '8px', padding: '10px 14px' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke={C.g300} strokeWidth="1.8" /><path d="M12 6v6l4 2" stroke={C.g300} strokeWidth="1.8" strokeLinecap="round" /></svg>
                  <input type="datetime-local" value={poll.endsAt} onChange={e => setPoll(p => ({ ...p, endsAt: e.target.value }))} style={{ flex: 1, border: 'none', outline: 'none', fontFamily: ff, fontSize: '14px', color: C.bk, background: 'transparent' }} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 첨부 파일 목록 */}
        {attachments.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {attachments.map(({ id, file }) => (
              <div key={id} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 10px', background: C.g200, borderRadius: '6px', maxWidth: '220px' }}>
                <span style={{ ...t('12px', 400, C.bk), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
                <button onClick={() => setAttachments(prev => prev.filter(a => a.id !== id))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, flexShrink: 0, display: 'flex' }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke={C.g400} strokeWidth="2.5" strokeLinecap="round" /></svg>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 푸터 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px', borderTop: `1px solid ${C.g200}` }}>
        <span style={t('12px', 400, C.g300)}>{content.length}/{charLimit} 자</span>
        <input ref={fileRef} type="file" multiple onChange={handleFiles} style={{ display: 'none' }} />
        <button onClick={() => fileRef.current?.click()} title="파일 첨부" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, display: 'flex', color: C.g300 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <span style={{ flex: 1 }} />
        <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: '8px', border: `1px solid ${C.g200}`, background: C.white, cursor: 'pointer', ...t('13px', 500, C.g400) }}>취소</button>
        <button style={{ padding: '8px 16px', borderRadius: '8px', border: `1px solid ${C.g200}`, background: C.white, cursor: 'default', ...t('13px', 500, C.g400) }}>임시저장</button>
        <button onClick={handleSubmit} disabled={isSubmitting} style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', background: isSubmitting ? C.g200 : C.bk, cursor: isSubmitting ? 'default' : 'pointer', ...t('13px', 600, isSubmitting ? C.g300 : C.white) }}>
          {isSubmitting ? '등록 중...' : '저장'}
        </button>
      </div>
    </div>
  );
}

// ─── Poll View ────────────────────────────────────────────────────────────────

function PollView({ poll, isLoggedIn, isTest, onVote, onCancelVote }) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [voting, setVoting] = useState(false);
  const hasVoted = poll.myVoteOptionIds?.length > 0;
  const showResults = hasVoted || poll.closed;

  const toggleOption = (id) => {
    if (poll.allowMultiple) {
      setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    } else {
      setSelectedIds(prev => prev.includes(id) ? [] : [id]);
    }
  };

  const handleVote = async () => {
    if (selectedIds.length === 0 || voting || !isLoggedIn) return;
    setVoting(true);
    try {
      await onVote(poll.id, selectedIds);
    } finally {
      setVoting(false);
    }
  };

  const handleCancel = async () => {
    if (voting) return;
    setVoting(true);
    try {
      await onCancelVote(poll.id);
      setSelectedIds([]);
    } finally {
      setVoting(false);
    }
  };

  const endsAtLabel = poll.endsAt
    ? new Date(poll.endsAt).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' })
    : null;

  return (
    <div style={{ border: `1px solid ${C.g200}`, borderRadius: '12px', overflow: 'hidden', marginBottom: '20px' }}>
      {/* 헤더 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '13px 18px', borderBottom: `1px solid ${C.g200}`, background: '#FAFAFA' }}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill={C.g300}><rect x="2" y="13" width="4" height="9" rx="1" /><rect x="9" y="8" width="4" height="14" rx="1" /><rect x="16" y="3" width="4" height="19" rx="1" /></svg>
        <span style={t('14px', 600, C.bk)}>{poll.question}</span>
        <span style={{ flex: 1 }} />
        {poll.closed && (
          <span style={{ padding: '2px 8px', background: '#F3F3F5', border: `1px solid #E0E0E5`, borderRadius: '5px', ...t('12px', 600, '#888896') }}>마감</span>
        )}
      </div>

      {/* 옵션 목록 */}
      <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {poll.options.map(opt => {
          const isMyVote = poll.myVoteOptionIds?.includes(opt.id);
          const isSelected = selectedIds.includes(opt.id);

          if (showResults) {
            return (
              <div key={opt.id} style={{ position: 'relative', borderRadius: '8px', overflow: 'hidden', border: `1.5px solid ${isMyVote ? C.bk : C.g200}` }}>
                {/* 퍼센트 바 배경 */}
                <div style={{ position: 'absolute', inset: 0, width: `${opt.percent ?? 0}%`, background: isMyVote ? 'rgba(9,9,9,0.08)' : '#F5F5F6', transition: 'width 0.4s ease' }} />
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {isMyVote && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke={C.bk} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    )}
                    <span style={t('14px', isMyVote ? 600 : 400, C.bk)}>{opt.text}</span>
                  </div>
                  <span style={t('13px', 600, C.bk)}>{opt.percent ?? 0}%</span>
                </div>
              </div>
            );
          }

          return (
            <button
              key={opt.id}
              onClick={() => toggleOption(opt.id)}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', borderRadius: '8px', cursor: 'pointer', border: `1.5px solid ${isSelected ? C.bk : C.g200}`, background: isSelected ? 'rgba(9,9,9,0.05)' : C.white, textAlign: 'left', width: '100%' }}
            >
              <div style={{ width: 16, height: 16, borderRadius: poll.allowMultiple ? '3px' : '50%', border: `1.5px solid ${isSelected ? C.bk : C.g200}`, background: isSelected ? C.bk : C.white, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {isSelected && <svg width="10" height="10" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke={C.white} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" /></svg>}
              </div>
              <span style={t('14px', 400, C.bk)}>{opt.text}</span>
            </button>
          );
        })}
      </div>

      {/* 푸터 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 18px', borderTop: `1px solid ${C.g200}` }}>
        <span style={t('12px', 400, C.g300)}>총 {(poll.totalVoteCount ?? 0).toLocaleString()}명 참여{endsAtLabel ? ` · ${endsAtLabel} 마감` : ''}</span>
        <span style={{ flex: 1 }} />
        {!poll.closed && isLoggedIn && !isTest && (
          hasVoted ? (
            <button onClick={handleCancel} disabled={voting} style={{ padding: '6px 14px', borderRadius: '7px', border: `1px solid ${C.g200}`, background: C.white, cursor: voting ? 'default' : 'pointer', ...t('13px', 500, C.g400) }}>
              {voting ? '...' : '투표 취소'}
            </button>
          ) : (
            <button onClick={handleVote} disabled={selectedIds.length === 0 || voting} style={{ padding: '6px 14px', borderRadius: '7px', border: 'none', background: selectedIds.length > 0 && !voting ? C.bk : C.g200, cursor: selectedIds.length > 0 && !voting ? 'pointer' : 'default', ...t('13px', 600, selectedIds.length > 0 && !voting ? C.white : C.g300) }}>
              {voting ? '...' : '투표하기'}
            </button>
          )
        )}
        {!poll.closed && isLoggedIn && isTest && (
          <span style={{ ...t('12px', 400, C.g300), fontStyle: 'italic' }}>테스트 모드에서는 저장되지 않습니다</span>
        )}
      </div>
    </div>
  );
}

// ─── Post Detail View ─────────────────────────────────────────────────────────

function PostDetail({ post, onCommentAdd, onCommentLikeToggle, onPostLikeToggle, onPostEdit, onPostDelete, onCommentDelete, onVote, onCancelVote, onPinToggle, isLoggedIn, isAdmin, isQnaAdmin, isTest, userName, userId, userProfileImage }) {
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editTitle, setEditTitle] = useState(post.title);
  const [editContent, setEditContent] = useState(post.content);
  const [editSaving, setEditSaving] = useState(false);
  const [copyToast, setCopyToast] = useState(false);

  const handleShare = async () => {
    const url = `${window.location.origin}/community/${post.id}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const el = document.createElement('textarea');
      el.value = url;
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopyToast(true);
    setTimeout(() => setCopyToast(false), 2000);
  };

  const isQna = post.category === 'QnA';
  const canComment = isLoggedIn && (!isQna || isQnaAdmin || isAdmin);
  const isAuthor = isLoggedIn && (
    post.isWriter === true ||
    (userId != null && post.authorId != null && userId === post.authorId) ||
    (userName != null && post.authorName != null && post.authorName === userName)
  );
  const canEdit = isAuthor || isAdmin || isQnaAdmin;

  const handleCommentSubmit = async () => {
    if (!commentText.trim() || submitting) return;
    setSubmitting(true);
    try {
      await onCommentAdd(commentText.trim());
      setCommentText('');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSave = async () => {
    if (!editTitle.trim() || !editContent.trim()) return;
    setEditSaving(true);
    try {
      await onPostEdit(post.id, { title: editTitle, content: editContent });
      setEditMode(false);
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      {/* 링크 복사 토스트 */}
      {copyToast && (
        <div style={{ position: 'fixed', top: 32, left: '50%', transform: 'translateX(-50%)', background: C.bk, color: C.white, padding: '12px 24px', borderRadius: '10px', ...t('14px', 500, C.white), zIndex: 99999, boxShadow: '0 4px 16px rgba(0,0,0,0.18)', pointerEvents: 'none' }}>
          링크가 클립보드에 복사됐습니다
        </div>
      )}
      <div style={{ background: C.white, border: `1px solid ${C.g200}`, borderRadius: '16px', padding: '32px', marginBottom: '12px' }}>
        {/* 태그 + 액션 버튼 */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div style={{ display: 'flex', gap: '6px' }}>
            <Tag label={post.category} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {/* 고정/해제 — admin 전용 */}
            {isAdmin && !editMode && (
              <button onClick={onPinToggle} title={post.pinned ? '고정 해제' : '공지 고정'} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 8, color: post.pinned ? '#EA580C' : C.g300 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill={post.pinned ? 'currentColor' : 'none'}>
                  <path d="M12 22v-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                  <path d="M5 10.5V9a7 7 0 0114 0v1.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                  <rect x="3" y="10" width="18" height="4" rx="2" stroke="currentColor" strokeWidth="1.8" fill={post.pinned ? 'currentColor' : 'none'}/>
                </svg>
              </button>
            )}
            {canEdit && !editMode && (
              <>
                <button onClick={() => { setEditTitle(post.title); setEditContent(post.content); setEditMode(true); }} title="수정" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 8, color: C.g300 }}>
                  {/* 연필 아이콘 */}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <button onClick={() => onPostDelete(post.id)} title="삭제" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 8, color: '#EF4444' }}>
                  {/* 휴지통 아이콘 */}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </>
            )}
            <button onClick={handleShare} title="링크 복사" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6 }}>
              <ShareIcon />
            </button>
          </div>
        </div>

        {/* 작성자 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
          <Avatar initial={post.authorInitial} image={post.authorProfileImage} size={32} />
          <span style={t('14px', 600, C.bk)}>{post.author}</span>
        </div>

        {/* 제목 + 내용 — 수정 모드 분기 */}
        {editMode ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
            <input
              value={editTitle}
              onChange={e => setEditTitle(e.target.value)}
              style={{ width: '100%', padding: '10px 14px', border: `1px solid ${C.g200}`, borderRadius: '10px', fontFamily: ff, fontSize: '18px', fontWeight: 700, letterSpacing: '-0.04em', outline: 'none', boxSizing: 'border-box', color: C.bk }}
            />
            <textarea
              value={editContent}
              onChange={e => setEditContent(e.target.value)}
              rows={8}
              style={{ width: '100%', padding: '12px 14px', border: `1px solid ${C.g200}`, borderRadius: '10px', fontFamily: ff, fontSize: '15px', letterSpacing: '-0.04em', outline: 'none', resize: 'vertical', boxSizing: 'border-box', lineHeight: '1.8', color: C.bk }}
            />
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={() => setEditMode(false)} style={{ padding: '8px 18px', borderRadius: '8px', border: `1px solid ${C.g200}`, background: C.white, cursor: 'pointer', ...t('14px', 500, C.g400) }}>취소</button>
              <button onClick={handleEditSave} disabled={editSaving} style={{ padding: '8px 18px', borderRadius: '8px', border: 'none', background: C.bk, cursor: 'pointer', ...t('14px', 600, C.white) }}>{editSaving ? '저장 중...' : '저장'}</button>
            </div>
          </div>
        ) : (
          <>
            <h2 style={{ ...t('22px', 700, C.bk), margin: '0 0 16px 0' }}>{post.title}</h2>
            <p style={{ ...t('15px', 400, C.bk), margin: '0 0 20px 0', lineHeight: '1.8', whiteSpace: 'pre-wrap' }}>{post.content}</p>

            {/* 첨부파일 */}
            {post.attachments?.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ ...t('12px', 600, C.g300), marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>첨부파일</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {post.attachments.map(f => (
                    <a key={f.id} href={f.url} download={f.originalName} target="_blank" rel="noreferrer"
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 14px', border: `1px solid ${C.g200}`, borderRadius: '8px', textDecoration: 'none', background: '#FAFAFA' }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                        <path d="M13 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V9z" stroke={C.g300} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M13 2v7h7" stroke={C.g300} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span style={t('13px', 400, C.bk)}>{f.originalName}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* 투표 */}
            {post.poll && (
              <PollView poll={post.poll} isLoggedIn={isLoggedIn} isTest={isTest} onVote={onVote} onCancelVote={onCancelVote} />
            )}
          </>
        )}

        {/* 하단 메타 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '20px', borderTop: `1px solid ${C.g200}` }}>
          <span style={t('13px', 400, C.g300)}>{post.date}</span>
          <span style={{ color: C.g200 }}>·</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <EyeIcon />
            <span style={t('13px', 400, C.g300)}>{post.views.toLocaleString()}</span>
          </div>
          <span style={{ color: C.g200 }}>·</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <CommentIcon />
            <span style={t('13px', 400, C.g300)}>{post.comments.length}</span>
          </div>
          <div
            style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '4px', cursor: isTest ? 'default' : 'pointer', opacity: isTest ? 0.4 : 1 }}
            onClick={isTest ? undefined : onPostLikeToggle}
            title={isTest ? '테스트 모드에서는 저장되지 않습니다' : undefined}
          >
            <HeartIcon filled={post.isLiked} color="#EF4444" />
            <span style={t('13px', 400, post.isLiked ? '#EF4444' : C.g300)}>{post.likes}</span>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {post.comments.map(comment => {
          const isCommentAuthor = isLoggedIn && (
            comment.isWriter === true ||
            (userId != null && comment.authorId != null && userId === comment.authorId) ||
            (userName != null && comment.authorName != null && comment.authorName === userName)
          );
          const canDeleteComment = isCommentAuthor || isAdmin || isQnaAdmin;
          return (
          <div key={comment.id} style={{ background: C.white, border: `1px solid ${C.g200}`, borderRadius: '16px', padding: '20px 24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <Avatar initial={comment.authorInitial} image={comment.authorProfileImage || (isCommentAuthor ? userProfileImage : null)} size={32} />
              <span style={t('14px', 600, C.bk)}>{comment.author}</span>
              <span style={t('13px', 400, C.g300)}>{comment.date}</span>
              {canDeleteComment && (
                <button onClick={() => onCommentDelete(comment.id)} title="댓글 삭제" style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: C.g300 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              )}
            </div>
            <p style={{ ...t('14px', 400, C.bk), margin: '0 0 12px 0', lineHeight: '1.6' }}>{comment.content}</p>
            <div
              style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: isTest ? 'default' : 'pointer', opacity: isTest ? 0.4 : 1 }}
              onClick={isTest ? undefined : () => onCommentLikeToggle(comment.id)}
              title={isTest ? '테스트 모드에서는 저장되지 않습니다' : undefined}
            >
              <HeartIcon filled={comment.isLiked} color="#EF4444" />
              <span style={t('13px', 400, comment.isLiked ? '#EF4444' : C.g300)}>{comment.likes}</span>
            </div>
          </div>
          );
        })}

        {canComment ? (
          <div style={{ background: C.white, border: `1px solid ${C.g200}`, borderRadius: '16px', padding: '16px 20px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
            <Avatar initial="나" image={userProfileImage} size={32} />
            <div style={{ flex: 1 }}>
              <input value={commentText} onChange={e => setCommentText(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) handleCommentSubmit(); }} placeholder="댓글을 입력하세요" style={{ width: '100%', border: 'none', outline: 'none', fontFamily: ff, fontSize: '14px', letterSpacing: '-0.04em', color: C.bk }} />
            </div>
            <button onClick={handleCommentSubmit} disabled={submitting || !commentText.trim()} style={{ padding: '6px 14px', borderRadius: '8px', border: 'none', background: commentText.trim() && !submitting ? C.bk : C.g200, cursor: commentText.trim() && !submitting ? 'pointer' : 'default', ...t('13px', 600, commentText.trim() && !submitting ? C.white : C.g300) }}>
              {submitting ? '...' : '등록'}
            </button>
          </div>
        ) : (
          <div style={{ background: '#F8F8FA', border: `1px solid ${C.g200}`, borderRadius: '16px', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="11" width="18" height="11" rx="2" stroke={C.g300} strokeWidth="1.8" />
              <path d="M7 11V7a5 5 0 0110 0v4" stroke={C.g300} strokeWidth="1.8" strokeLinecap="round" />
            </svg>
            <span style={t('14px', 400, C.g300)}>
              {!isLoggedIn ? '댓글을 작성하려면 로그인이 필요합니다.' : 'QnA 답변은 캐스타 운영팀만 작성할 수 있습니다.'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Post Card (읽으면 fade-out 애니메이션으로 배경 전환) ────────────────────

function PostCard({ post, onClick, isRead }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onClick={() => onClick(post)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: isRead ? '#F5F5F6' : C.white,
        border: `1px solid ${C.g200}`,
        borderRadius: '20px',
        padding: '22px 28px',
        cursor: 'pointer',
        transition: 'box-shadow 0.15s, background 0.4s ease',
        boxShadow: hovered ? C.shadow : 'none',
      }}
    >
      {/* 태그 행 */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
        {post.pinned && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '3px 8px', background: '#FFF4ED', borderRadius: '6px', ...t('12px', 600, '#EA580C') }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M12 22v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><path d="M5 10.5V9a7 7 0 0114 0v1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><rect x="3" y="10" width="18" height="4" rx="2"/></svg>
            고정
          </span>
        )}
        {post.isAd ? <CommunityAdBadge /> : <Tag label={post.category} />}
        {post.hasPoll && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '3px 8px', background: '#F0F0FF', borderRadius: '6px', ...t('12px', 600, '#5856D6') }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><rect x="2" y="13" width="4" height="9" rx="1" /><rect x="9" y="8" width="4" height="14" rx="1" /><rect x="16" y="3" width="4" height="19" rx="1" /></svg>
            투표
          </span>
        )}
      </div>

      {/* 제목 */}
      <div style={{ ...t('20px', 600, C.bk), marginBottom: '6px' }}>{post.title}</div>

      {/* 내용 미리보기 */}
      <div style={{ ...t('14px', 400, C.g300), marginBottom: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {post.content}
      </div>

      {/* 메타 행: 작성자 / 조회·좋아요·댓글·날짜·미읽음 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Avatar initial={post.authorInitial} image={post.authorProfileImage} size={24} />
        <span style={t('13px', 500, C.bk)}>{post.author}</span>
        <span style={{ flex: 1 }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
          <EyeIcon />
          <span style={t('13px', 400, C.g300)}>{post.views.toLocaleString()}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '3px', marginLeft: 4 }}>
          <HeartIcon filled={post.isLiked} color="#EF4444" />
          <span style={t('13px', 400, post.isLiked ? '#EF4444' : C.g300)}>{post.likes}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '3px', marginLeft: 4 }}>
          <CommentIcon />
          <span style={t('13px', 400, C.g300)}>{post.commentCount ?? 0}</span>
        </div>
        <span style={{ ...t('13px', 400, C.g300), marginLeft: 8 }}>{post.date}</span>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function CommunityPage() {
  // 전체 카테고리별 posts를 캐시 — 카테고리 전환 시 다른 카테고리 미읽음 유지
  const allPostsCache = useRef({});

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listTransition, setListTransition] = useState(false); // 목록 fade 전환
  const [writeLoading, setWriteLoading] = useState(false);

  const location = useLocation();
  const [activeCategory, setActiveCategory] = useState(() => location.state?.category || '전체');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [writeOpen, setWriteOpen] = useState(false);

  const [selectedPost, setSelectedPost] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [readIds, setReadIds] = useState(loadReadIds);
  const [catDropOpen, setCatDropOpen] = useState(false);

  const { isLoggedIn, isAdmin, isQnaAdmin, isTest, userName, userId, userProfileImage } = useAuth();
  const navigate = useNavigate();
  const { postId: urlPostId } = useParams();

  const loadPosts = useCallback(async (category, searchVal, animate = true) => {
    if (animate) setListTransition(true);
    setLoading(true);
    try {
      // "Castar Ads"는 게시판 API가 아니라 광고 목록(GET /api/ads)을 그대로 보여준다.
      // 노출 기간이 지났거나 숨긴 광고는 서버가 빼고 주며, 서버가 없으면 프론트 상수로 되돌아간다.
      if (category === ADS_CATEGORY) {
        const adPosts = (await loadActiveAds())
          .filter(ad => !searchVal || ad.title.toLowerCase().includes(searchVal.toLowerCase()))
          .map(adToPostShape);
        allPostsCache.current[category] = adPosts;
        setPosts(adPosts);
        return;
      }
      const apiCategory = category === '전체' ? undefined : toApiCategory(category);
      const res = await getPosts({ category: apiCategory, search: searchVal || undefined, page: 1, size: 50 });
      const content = Array.isArray(res) ? res : (res.content ?? []);
      const normalized = content.map(normalizePost);

      // 카테고리별 캐시 저장 (검색 없을 때만)
      if (!searchVal) {
        allPostsCache.current[category] = normalized;
        // '전체' 응답에는 모든 카테고리 글이 섞여 있으므로, 각 카테고리 캐시도 함께 채운다
        if (category === '전체') {
          CATEGORIES.forEach(cat => {
            if (cat === '전체') return;
            allPostsCache.current[cat] = normalized.filter(p => p.category === cat);
          });
        }
      }

      setPosts(normalized);
    } catch (e) {
      console.error('[Community] 목록 로드 실패', e);
    } finally {
      setLoading(false);
      // fade-in
      setTimeout(() => setListTransition(false), 50);
    }
  }, []);

  useEffect(() => {
    if (search === appliedSearch) return;
    const timer = setTimeout(() => setAppliedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search, appliedSearch]);

  useEffect(() => {
    loadPosts(activeCategory, appliedSearch);
  }, [loadPosts, activeCategory, appliedSearch]);

  // 로그인 상태일 때 서버 읽음 기록 불러와 localStorage와 병합
  useEffect(() => {
    if (!isLoggedIn) return;
    getReadPostIds()
      .then(res => {
        const serverIds = res.readPostIds ?? [];
        if (!serverIds.length) return;
        setReadIds(prev => {
          const merged = new Set(prev);
          serverIds.forEach(id => merged.add(id));
          saveReadIds(merged);
          return merged;
        });
      })
      .catch(() => {});
  }, [isLoggedIn]);

  // 카테고리별 미읽음 수 — 캐시에서 계산 (광고 탭은 읽음/안읽음 개념을 적용하지 않음)
  const unreadCounts = {};
  CATEGORIES.forEach(cat => {
    if (cat === ADS_CATEGORY) { unreadCounts[cat] = 0; return; }
    const cached = allPostsCache.current[cat] || [];
    unreadCounts[cat] = cached.filter(p => !readIds.has(p.id)).length;
  });
  // 현재 목록도 반영
  if (activeCategory !== ADS_CATEGORY) {
    const currentCached = allPostsCache.current[activeCategory] || posts;
    unreadCounts[activeCategory] = currentCached.filter(p => !readIds.has(p.id)).length;
  }

  // URL의 postId로 진입 시 상세 자동 로드
  useEffect(() => {
    if (!urlPostId) {
      setSelectedPost(null);
      return;
    }
    const id = Number(urlPostId);
    setDetailLoading(true);
    setReadIds(prev => {
      const next = new Set(prev).add(id);
      saveReadIds(next);
      return next;
    });
    getPost(id)
      .then(detail => setSelectedPost(normalizePost(detail)))
      .catch(() => navigate('/community', { replace: true }))
      .finally(() => setDetailLoading(false));
  }, [urlPostId]); // eslint-disable-line react-hooks/exhaustive-deps

  // 게시글 열람 시 서버 읽음 기록 (로그인 상태 변화와 분리)
  useEffect(() => {
    if (!urlPostId || !isLoggedIn) return;
    markPostRead(Number(urlPostId)).catch(() => {});
  }, [urlPostId, isLoggedIn]);

  const handleCategoryChange = (cat) => {
    if (cat === activeCategory) return;
    navigate('/community');
    setActiveCategory(cat);
  };

  const handleSelectPost = (post) => {
    if (post.isAd) {
      navigate(`/community/ads/${post.id}`);
      return;
    }
    // urlPostId useEffect가 getPost를 호출하므로 여기서는 navigate만
    navigate(`/community/${post.id}`);
  };

  const handleWrite = async ({ title, content, category, fileIds, poll }) => {
    setWriteLoading(true);
    try {
      await createPost({ title, content, category: toApiCategory(category), fileIds, poll });
      setWriteOpen(false);
      // 현재 카테고리 재로드 후, 전체 카테고리 캐시도 백그라운드 갱신
      allPostsCache.current = {};
      await loadPosts(activeCategory, '', false);
      if (activeCategory !== '전체') loadPosts('전체', '', false).catch(() => {});
    } catch (e) {
      alert(e?.response?.data?.message || '글 등록에 실패했습니다.');
    } finally {
      setWriteLoading(false);
    }
  };

  const handlePostLikeToggle = async () => {
    if (!selectedPost) return;
    if (isTest) { alert('테스트 모드에서는 저장되지 않습니다.'); return; }
    try {
      const res = await togglePostLike(selectedPost.id);
      const likes = Math.max(0, res.likeCount);
      setSelectedPost(prev => ({ ...prev, isLiked: res.liked, likes }));
      setPosts(prev => prev.map(p => p.id === selectedPost.id ? { ...p, isLiked: res.liked, likes } : p));
    } catch (e) {
      if (e?.response?.status === 401) alert('로그인이 필요합니다.');
    }
  };

  const handlePinToggle = async () => {
    if (!selectedPost) return;
    try {
      if (selectedPost.pinned) {
        await unpinPost(selectedPost.id);
      } else {
        await pinPost(selectedPost.id);
      }
      const next = !selectedPost.pinned;
      setSelectedPost(prev => ({ ...prev, pinned: next }));
      setPosts(prev => prev.map(p => p.id === selectedPost.id ? { ...p, pinned: next } : p));
    } catch (e) {
      alert('고정 설정에 실패했습니다.');
    }
  };

  const handleVote = async (pollId, optionIds) => {
    if (!selectedPost) return;
    if (isTest) { alert('테스트 모드에서는 저장되지 않습니다.'); return; }
    try {
      await votePoll(pollId, optionIds);
      const raw = await getPost(selectedPost.id);
      setSelectedPost(normalizePost(raw));
    } catch (e) {
      if (e?.response?.status === 401) alert('로그인이 필요합니다.');
      else alert('투표에 실패했습니다.');
    }
  };

  const handleCancelVote = async (pollId) => {
    if (!selectedPost) return;
    try {
      await cancelVotePoll(pollId);
      const raw = await getPost(selectedPost.id);
      setSelectedPost(normalizePost(raw));
    } catch (e) {
      alert('투표 취소에 실패했습니다.');
    }
  };

  const handleCommentAdd = async (text) => {
    if (!selectedPost) return;
    try {
      const newComment = await createComment(selectedPost.id, { content: text });
      const normalized = normalizeComment(newComment);
      setSelectedPost(prev => ({ ...prev, comments: [...prev.comments, { ...normalized, authorProfileImage: normalized.authorProfileImage || userProfileImage || null }] }));
    } catch (e) {
      const msg = e?.response?.data?.message;
      if (e?.response?.status === 403) alert(msg || 'QnA 답변은 캐스타 운영팀만 작성할 수 있습니다.');
      else if (e?.response?.status === 401) alert('로그인이 필요합니다.');
      else alert('댓글 등록에 실패했습니다.');
      throw e;
    }
  };

  const handleCommentLikeToggle = async (commentId) => {
    if (isTest) { alert('테스트 모드에서는 저장되지 않습니다.'); return; }
    try {
      const res = await toggleCommentLike(commentId);
      const likes = Math.max(0, res.likeCount);
      setSelectedPost(prev => ({ ...prev, comments: prev.comments.map(c => c.id === commentId ? { ...c, isLiked: res.liked, likes } : c) }));
    } catch (e) {
      if (e?.response?.status === 401) alert('로그인이 필요합니다.');
    }
  };

  const handlePostEdit = async (postId, data) => {
    try {
      await updatePost(postId, data);
      const refreshed = await getPost(postId);
      const normalized = normalizePost(refreshed);
      setSelectedPost(normalized);
      setPosts(prev => prev.map(p => p.id === postId ? normalized : p));
    } catch (e) {
      alert('수정에 실패했습니다.');
    }
  };

  const handlePostDelete = async (postId) => {
    if (!window.confirm('게시글을 삭제하시겠습니까?')) return;
    try {
      await deletePost(postId);
      setPosts(prev => prev.filter(p => p.id !== postId));
      navigate('/community');
    } catch (e) {
      alert('삭제에 실패했습니다.');
    }
  };

  const handleCommentDelete = async (commentId) => {
    if (!window.confirm('댓글을 삭제하시겠습니까?')) return;
    try {
      await deleteComment(commentId);
      setSelectedPost(prev => ({ ...prev, comments: prev.comments.filter(c => c.id !== commentId) }));
    } catch (e) {
      alert('댓글 삭제에 실패했습니다.');
    }
  };

  return (
    <PageLayout>
      <div className={styles.pageWrapper}>
        <div className={styles.pageHeader}>
          <div>
            <h1 style={{ ...t('40px', 700, C.bk), margin: 0 }}>커뮤니티</h1>
            <span style={t('16px', 400, C.sub)}>다양한 사람들과 자유롭게 소통하며 캐스팅, 촬영에 대한 정보와 경험을 나눠보세요.</span>
          </div>
          {activeCategory !== ADS_CATEGORY && (
            <WriteButton onClick={() => isLoggedIn ? setWriteOpen(true) : navigate('/login')} />
          )}
        </div>

        {/* ── 모바일 카테고리 드롭다운 ── */}
        <div className={styles.mobileCatDropdown}>
          <button
            className={styles.mobileCatTrigger}
            onClick={() => setCatDropOpen(o => !o)}
          >
            <span style={t('15px', 600, C.bk)}>{activeCategory}</span>
            <svg
              width="16" height="16" viewBox="0 0 24 24" fill="none"
              style={{ transform: catDropOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
            >
              <path d="M6 9l6 6 6-6" stroke={C.bk} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          {catDropOpen && (
            <div className={styles.mobileCatList}>
              {CATEGORIES.map(cat => {
                const isActive = activeCategory === cat;
                const count = unreadCounts[cat] || 0;
                return (
                  <div
                    key={cat}
                    className={`${styles.mobileCatItem}${isActive ? ' ' + styles.mobileCatItemActive : ''}`}
                    onClick={() => { handleCategoryChange(cat); setCatDropOpen(false); }}
                  >
                    <span>{cat}</span>
                    {count > 0 && (
                      <span className={`${styles.navCatCardBadge}${isActive ? ' ' + styles.navCatCardBadgeActive : ''}`}>{count}</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className={styles.contentLayout}>
          {/* ── 왼쪽 사이드바 (데스크탑) ── */}
          <div className={styles.sidebar}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                  <SearchIcon />
                </span>
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="검색"
                  style={{ width: '100%', padding: '10px 12px 10px 38px', border: `1px solid ${C.g200}`, borderRadius: '10px', fontFamily: ff, fontSize: '14px', outline: 'none', boxSizing: 'border-box', color: C.bk }}
                />
              </div>
            </div>

            <div className={styles.navCard}>
              <div style={{ ...t('20px', 700, C.bk), marginBottom: '12px' }}>커뮤니티 페이지</div>
              <div className={styles.navCatCardList} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {CATEGORIES.map(cat => {
                const isActive = activeCategory === cat;
                const count = unreadCounts[cat] || 0;
                return (
                  <div
                    key={cat}
                    className={`${styles.navCatCard}${isActive ? ' ' + styles.navCatCardActive : ''}`}
                    onClick={() => handleCategoryChange(cat)}
                  >
                    <span className={`${styles.navCatCardLabel}${isActive ? ' ' + styles.navCatCardLabelActive : ''}`}>{cat}</span>
                    {count > 0 && (
                      <span className={`${styles.navCatCardBadge}${isActive ? ' ' + styles.navCatCardBadgeActive : ''}`}>
                        {count}
                      </span>
                    )}
                  </div>
                );
              })}
              </div>
            </div>
          </div>

          {/* ── 오른쪽: 목록 or 상세 ── */}
          <div className={styles.mainContent}>
            {selectedPost ? (
              detailLoading ? (
                <ListLoader />
              ) : (
                <>
                  <button
                    onClick={() => navigate('/community')}
                    className={styles.backBtn}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M19 12H5M5 12l7 7M5 12l7-7" stroke={C.g400} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    목록으로 돌아가기
                  </button>
                  <PostDetail
                    post={selectedPost}
                    onPostLikeToggle={handlePostLikeToggle}
                    onCommentAdd={handleCommentAdd}
                    onCommentLikeToggle={handleCommentLikeToggle}
                    onPostEdit={handlePostEdit}
                    onPostDelete={handlePostDelete}
                    onCommentDelete={handleCommentDelete}
                    onVote={handleVote}
                    onCancelVote={handleCancelVote}
                    onPinToggle={handlePinToggle}
                    isLoggedIn={isLoggedIn}
                    isAdmin={isAdmin}
                    isQnaAdmin={isQnaAdmin}
                    isTest={isTest}
                    userName={userName}
                    userId={userId}
                    userProfileImage={userProfileImage}
                  />
                </>
              )
            ) : (
              <>
                {writeOpen && activeCategory !== ADS_CATEGORY && (
                  <WritePanel onClose={() => setWriteOpen(false)} onSubmit={handleWrite} loading={writeLoading} isAdmin={isAdmin} isQnaAdmin={isQnaAdmin} />
                )}
                <div style={{ opacity: listTransition ? 0 : 1, transition: 'opacity 0.2s ease' }}>
                  {loading ? (
                    <ListLoader />
                  ) : posts.length === 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '100px 0', gap: '16px' }}>
                      <svg width="56" height="56" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke={C.g200} strokeWidth="1.5" />
                        <path d="M12 8v4M12 16h.01" stroke={C.g300} strokeWidth="2" strokeLinecap="round" />
                      </svg>
                      {activeCategory === ADS_CATEGORY ? (
                        <>
                          <div style={t('17px', 700, C.bk)}>진행 중인 광고가 없어요</div>
                          <div style={t('14px', 400, C.sub)}>새로운 광고가 열리면 이곳에서 확인할 수 있어요.</div>
                        </>
                      ) : (
                        <>
                          <div style={t('17px', 700, C.bk)}>아직 게시글이 없어요</div>
                          <div style={t('14px', 400, C.sub)}>첫 번째 글을 작성해 보세요!</div>
                          <WriteButton onClick={() => isLoggedIn ? setWriteOpen(true) : navigate('/login')} dark />
                        </>
                      )}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {[...posts].sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0)).map(post => (
                        <PostCard
                          key={post.id}
                          post={post}
                          onClick={handleSelectPost}
                          isRead={readIds.has(post.id)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* 커뮤니티 페이지 하단 텍스트 - 상단으로 이동 */}
      </div>
    </PageLayout>
  );
}
